import { useCallback, useEffect, useRef, useState } from 'react';
import WebSocketClient from '../services/websocket';
import {
  WebSocketLockData,
  WebSocketMessage,
  WebSocketUnlockData,
  WebSocketUpdateData,
} from '../types/websocket';

interface LockedNode {
  userId: string;
  username: string;
}

interface UseWebSocketProps<T = any> {
  sessionId: string | number | null;
  userId: string;
  username: string;
  fullName: string;
  onUpdateMessage?: (suggestions: T[]) => void;
}

export const useWebSocket = <T = any>({
  sessionId,
  userId,
  username,
  fullName,
  onUpdateMessage,
}: UseWebSocketProps<T>) => {
  const wsClientRef = useRef<WebSocketClient | null>(null);
  const [lockedNodes, setLockedNodes] = useState<Record<string, LockedNode>>({});

  const handleLockMessage = useCallback((data: WebSocketLockData) => {
    setLockedNodes((prev) => {
      const existingNode = prev[data.data.nodeId];
      if (
        existingNode?.userId === data.data.userId &&
        existingNode?.username === data.data.username
      ) {
        return prev;
      }
      return {
        ...prev,
        [data.data.nodeId]: {
          userId: data.data.userId,
          username: data.data.username,
        },
      };
    });
  }, []);

  const handleUnlockMessage = useCallback((data: WebSocketUnlockData) => {
    setLockedNodes((prev) => {
      const updated = { ...prev };
      delete updated[data.data.nodeId];
      return updated;
    });
  }, []);

  const handleUpdateMessage = useCallback(
    (data: WebSocketUpdateData) => {
      if (!data.data.suggestions.length) {
        return;
      }

      // Call the callback if provided
      if (onUpdateMessage) {
        onUpdateMessage(data.data.suggestions as any[]);
      }

      // Still return the suggestions for backward compatibility
      return data.data.suggestions;
    },
    [onUpdateMessage],
  );

  const handleWebSocketMessage = useCallback(
    (data: WebSocketMessage) => {
      switch (data.type) {
        case 'LOCK':
          handleLockMessage(data);
          break;
        case 'UNLOCK':
          handleUnlockMessage(data);
          break;
        case 'UPDATE':
          return handleUpdateMessage(data);
        default:
          console.log('Unhandled message type:', (data as any).type);
      }
    },
    [handleLockMessage, handleUnlockMessage, handleUpdateMessage],
  );

  // Initialize WebSocket connection
  useEffect(() => {
    if (sessionId) {
      const wsClient = new WebSocketClient(sessionId.toString(), handleWebSocketMessage);
      wsClientRef.current = wsClient;

      return () => {
        wsClient.close();
        wsClientRef.current = null;
      };
    }
  }, [sessionId, handleWebSocketMessage]);

  // Lock a node
  const lockNode = useCallback(
    (nodeId: string) => {
      if (wsClientRef.current && userId && username) {
        wsClientRef.current.lockNode(nodeId, userId, fullName);
      }
    },
    [userId, username, fullName],
  );

  // Unlock a node
  const unlockNode = useCallback(
    (nodeId: string) => {
      if (wsClientRef.current && userId && username) {
        wsClientRef.current.unlockNode(nodeId, userId, fullName);
      }
    },
    [userId, username, fullName],
  );

  // Check if a node is locked by the current user
  const isLockedByCurrentUser = useCallback(
    (nodeId: string) => {
      return lockedNodes[nodeId] && lockedNodes[nodeId].userId === userId;
    },
    [lockedNodes, userId],
  );

  // Check if a node is locked by another user
  const isLockedByOtherUser = useCallback(
    (nodeId: string) => {
      return lockedNodes[nodeId] && lockedNodes[nodeId].userId !== userId;
    },
    [lockedNodes, userId],
  );

  // Get user who locked a node
  const getNodeLocker = useCallback(
    (nodeId: string) => {
      return lockedNodes[nodeId];
    },
    [lockedNodes],
  );

  return {
    lockedNodes,
    lockNode,
    unlockNode,
    isLockedByCurrentUser,
    isLockedByOtherUser,
    getNodeLocker,
  };
};
